/**
 * 日時整形ユーティリティ（JST 表示）
 *
 * バックエンドは原則タイムゾーンオフセット付き（+09:00）の ISO 文字列を返すが、
 * オフセットの付かない naive 文字列が来ても JST とみなして扱う多層防御を入れる。
 * `new Date("2026-07-21T01:00:00")`（オフセットなし）はブラウザのローカル時刻として
 * 解釈されるため、JST 以外の端末では時刻がずれる。ここで明示的に JST を補い、
 * かつ表示も `timeZone: 'Asia/Tokyo'` で固定してブラウザ TZ 非依存にする。
 */

/** 文字列末尾にタイムゾーン情報（Z または ±hh:mm）を含むか */
const hasTimezone = (value: string): boolean =>
  /(?:Z|[+-]\d{2}:?\d{2})$/.test(value.trim());

/**
 * 日時文字列を日本時間の表示用文字列（例: 2026/7/21 10:00:00）へ整形する。
 * @param value ISO 日時文字列（オフセット有無どちらも可）
 * @param fallback 値が空/不正なときの表示（既定 '-'）
 */
export const formatJstDateTime = (
  value: string | null | undefined,
  fallback = '-'
): string => {
  if (!value) return fallback;
  const normalized = hasTimezone(value) ? value : `${value}+09:00`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
};
